import { useMutation, useQuery } from '@apollo/client';
import {
    ARCHIVE_MESSAGE_DRAFTS,
    GET_AUTO_GENERATE_DRAFT_DATA,
    GET_LATEST_MESSAGE_DRAFT_ONE,
    INSERT_MESSAGE_DRAFT,
    UPDATE_MESSAGE_DRAFT,
} from '@zaplify/graphql';
import { useAuth } from '../../providers/authentication-provider';
import { ComposerMessage } from './message-composer/composer-message';
import { useMemo } from 'react';
import { ChannelType } from '../../types/channel-type';

export const useDraftMessagesV2 = ({ contactId }: { contactId: string }) => {
    const { authState } = useAuth();

    const [updateDraftMessage] = useMutation(UPDATE_MESSAGE_DRAFT);
    const [insertDraftMessage] = useMutation(INSERT_MESSAGE_DRAFT);
    const [archiveDraftMessages] = useMutation(ARCHIVE_MESSAGE_DRAFTS);
    const { data: draftMessageDb } = useQuery(GET_LATEST_MESSAGE_DRAFT_ONE, {
        variables: {
            contactId,
        },
        skip: !contactId,
    });

    const setDraftMessage = async ({
        message,
        source,
        userPrompt,
    }: {
        message: ComposerMessage;
        source: 'user' | 'auto-generate' | 'regenerate';
        userPrompt?: string;
    }) => {
        if (draftMessageDb?.MessageDrafts?.[0] && source === 'user') {
            await updateDraftMessage({
                variables: { id: draftMessageDb.MessageDrafts[0].id, ...message, userPrompt: '' },
                refetchQueries: [
                    { query: GET_LATEST_MESSAGE_DRAFT_ONE, variables: { contactId } },
                    { query: GET_AUTO_GENERATE_DRAFT_DATA, variables: { contactId } },
                ],
            });
        } else {
            await insertDraftMessage({
                variables: {
                    content: message.content || '',
                    subjectLine: message.subjectLine || '',
                    userPrompt: userPrompt || '',
                    userId: authState.user?.id,
                    organizationId: authState.userOrganizationId,
                    contactId,
                    source: source,
                    channelType: message.channelType,
                },
                refetchQueries: [
                    { query: GET_LATEST_MESSAGE_DRAFT_ONE, variables: { contactId } },
                    { query: GET_AUTO_GENERATE_DRAFT_DATA, variables: { contactId } },
                ],
            });
        }
    };

    const removeDraftMessage = () => {
        if (draftMessageDb?.MessageDrafts?.[0]) {
            archiveDraftMessages({
                variables: { userId: authState.user?.id, contactId },
                refetchQueries: [
                    { query: GET_LATEST_MESSAGE_DRAFT_ONE, variables: { contactId } },
                    { query: GET_AUTO_GENERATE_DRAFT_DATA, variables: { contactId } },
                ],
            });
        }
    };

    const draftMessage: ComposerMessage | null = useMemo(() => {
        return {
            content: draftMessageDb?.MessageDrafts?.[0]?.content || '',
            subjectLine: draftMessageDb?.MessageDrafts?.[0]?.subjectLine || '',
            channelType: draftMessageDb?.MessageDrafts?.[0]?.channelType || ChannelType.LINKEDIN,
        };
    }, [draftMessageDb]);

    return {
        draftMessage,
        setDraftMessage,
        removeDraftMessage,
    };
};
